import { ChatChannel, t } from '@viral-space/shared';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  system: '#aaaaaa',
  bg: 'rgba(0,16,0,0.6)',
  dim: 'rgba(0,255,65,0.3)',
} as const;

const FONT = "'Share Tech Mono', monospace";

const CHANNEL_COLORS: Record<ChatChannel, string> = {
  [ChatChannel.Global]: '#ffe34d',
  [ChatChannel.Sector]: '#00ccff',
  [ChatChannel.Direct]: '#ff88ff',
  [ChatChannel.Local]: '#00ff41',
  [ChatChannel.Fleet]: '#ffb000',
  [ChatChannel.System]: COLORS.system,
};

const CHANNEL_KEYS: Record<ChatChannel, string> = {
  [ChatChannel.Global]: 'chat.global',
  [ChatChannel.Sector]: 'chat.sector',
  [ChatChannel.Direct]: 'chat.direct',
  [ChatChannel.Local]: 'chat.local',
  [ChatChannel.Fleet]: 'chat.fleet',
  [ChatChannel.System]: 'SYS',
};

const VISIBLE_CHANNELS: ChatChannel[] = [
  ChatChannel.Global,
  ChatChannel.Sector,
  ChatChannel.Direct,
];

const MAX_MESSAGES = 200;
const FADE_TIMEOUT = 9000;
const DM_COMMAND = /^\/(w|msg|whisper)\s+(\S+)\s+([\s\S]+)$/i;

type SendHandler = (
  content: string,
  channel: ChatChannel,
  targetName?: string,
) => void;

interface StoredMessage {
  senderName: string;
  channel: ChatChannel;
  content: string;
  timestamp: number;
  /** True when the local player authored the message. */
  fromLocal: boolean;
  /** DM partner name ("other side of the conversation"), for Direct. */
  dmPartner?: string;
  /** Kind of system hint (so we can localize error reasons). */
  system?: SystemMessageKind;
  /**
   * Server-tagged Premium snapshot. Drives the gold ★ badge before
   * the callsign in the message line. Stored on the message itself
   * so chat history rendered after the sender lapses keeps the
   * correct badge state for older lines.
   */
  premium?: boolean;
}

type SystemMessageKind =
  | { kind: 'error'; key: string; params?: Record<string, string> }
  | { kind: 'info'; text: string }
  | { kind: 'activity'; text: string; color?: string };

export class Chat {
  private container: HTMLElement;
  private root!: HTMLElement;
  private messageArea!: HTMLElement;
  private input!: HTMLInputElement;
  private inputRow!: HTMLElement;
  private hintRow!: HTMLElement;
  private tabContainer!: HTMLElement;
  private tabButtons = new Map<ChatChannel, HTMLElement>();
  private dmTargetRow!: HTMLElement;
  private dmTargetInput!: HTMLInputElement;

  private messages: StoredMessage[] = [];
  private activeChannel: ChatChannel = ChatChannel.Global;
  private inputActive = false;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private enabled = false;
  private dmTarget = '';
  private localName = '';

  sendMessage: SendHandler | null = null;
  onInputFocusChange: ((focused: boolean) => void) | null = null;
  /** Wired in main.ts — opens the full-window comms panel. */
  onExpand: (() => void) | null = null;

  private isMobile = false;

  constructor(container: HTMLElement, opts: { isMobile?: boolean } = {}) {
    this.container = container;
    this.isMobile = !!opts.isMobile;
    this.build();
    this.updateHint();
    this.root.style.display = 'none';
    this.scheduleFade();
  }

  setLocalPlayer(name: string): void {
    this.localName = name;
  }

  private css(el: HTMLElement, styles: Partial<CSSStyleDeclaration>) {
    Object.assign(el.style, styles);
  }

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    this.css(e, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build() {
    // On mobile landscape we anchor the chat under the top-left HP /
    // Shield / Energy stack instead of floating above the radar — the
    // bottom-left is needed for the thrust joystick there. Desktop
    // keeps its original above-radar placement.
    const positionStyles: Partial<CSSStyleDeclaration> = this.isMobile
      ? {
          // Match the top-left HUD stack, which uses plain `left: 16px`
          // without safe-area padding. Using the same value keeps the
          // chat border flush with HULL / SHIELD / ENERGY labels on
          // every device (notched iPhones would otherwise shift the
          // chat inward by the inset amount).
          top: 'calc(72px + env(safe-area-inset-top, 0px))',
          left: '16px',
          width: 'min(42vw, 320px)',
          maxHeight: '38vh',
        }
      : {
          bottom: 'clamp(210px, 22vw, 280px)',
          left: '16px',
          width: 'clamp(260px, 28vw, 420px)',
          maxHeight: 'clamp(180px, 24vh, 320px)',
        };
    this.root = this.el('div', {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: FONT,
      color: COLORS.primary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontSize: '11px',
      zIndex: '20',
      transition: 'opacity 0.5s ease',
      pointerEvents: 'none',
      ...positionStyles,
    });
    this.container.appendChild(this.root);

    this.tabContainer = this.el('div', {
      display: 'flex',
      gap: '1px',
      flexShrink: '0',
      pointerEvents: 'auto',
    });
    this.root.appendChild(this.tabContainer);

    for (const ch of VISIBLE_CHANNELS) {
      const btn = this.el('button', {
        fontFamily: FONT,
        fontSize: '9px',
        padding: '3px 10px',
        background: 'transparent',
        border: `1px solid ${COLORS.dim}`,
        borderBottom: 'none',
        color: COLORS.dim,
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        transition: 'all 0.15s',
        pointerEvents: 'auto',
      }, t(CHANNEL_KEYS[ch]));

      btn.addEventListener('click', () => {
        this.setActiveChannel(ch);
        this.revealAndResetFade();
      });
      btn.addEventListener('mouseenter', () => {
        this.revealAndResetFade();
      });

      this.tabContainer.appendChild(btn);
      this.tabButtons.set(ch, btn);
    }

    // Expand-to-full-window affordance, pinned to the right of the tab
    // strip. Sized up slightly on mobile so it's a comfortable tap
    // target next to the small channel tabs.
    const expandBtn = this.el('button', {
      fontFamily: FONT,
      fontSize: this.isMobile ? '12px' : '10px',
      padding: this.isMobile ? '3px 12px' : '3px 10px',
      marginLeft: 'auto',
      background: COLORS.bg,
      // Amber accent (vs the dim green tabs) so the entry point to the
      // full comms window is discoverable at a glance.
      border: '1px solid rgba(255,176,0,0.65)',
      borderBottom: 'none',
      color: COLORS.secondary,
      cursor: 'pointer',
      letterSpacing: '0.5px',
      textShadow: '0 0 4px rgba(255,176,0,0.5)',
      transition: 'all 0.15s',
      pointerEvents: 'auto',
      touchAction: 'manipulation',
    }, '⛶');
    expandBtn.title = t('chat.expand');
    expandBtn.setAttribute('aria-label', t('chat.expand'));
    expandBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onExpand?.();
    });
    expandBtn.addEventListener('mouseenter', () => {
      expandBtn.style.color = '#ffd24d';
      expandBtn.style.borderColor = COLORS.secondary;
      this.revealAndResetFade();
    });
    expandBtn.addEventListener('mouseleave', () => {
      expandBtn.style.color = COLORS.secondary;
      expandBtn.style.borderColor = 'rgba(255,176,0,0.65)';
    });
    this.tabContainer.appendChild(expandBtn);

    this.messageArea = this.el('div', {
      flex: '1',
      overflowY: 'auto',
      background: COLORS.bg,
      border: `1px solid ${COLORS.dim}`,
      padding: '6px 8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      pointerEvents: 'auto',
      scrollBehavior: 'smooth',
      ...(this.isMobile ? { cursor: 'pointer' } : {}),
    });
    this.messageArea.style.scrollbarWidth = 'thin';
    this.messageArea.style.scrollbarColor = `${COLORS.dim} transparent`;
    if (this.isMobile) {
      // The dedicated CHAT button was removed from the top-bar pills;
      // on mobile, tapping anywhere on the message area opens the
      // compose input so the chat panel itself is the affordance.
      // `pointerdown` activates on first contact (no 300ms tap delay),
      // and we only act when the input is not already active so the
      // pilot doesn't accidentally dismiss their half-typed message
      // by tapping the transcript to scroll.
      this.messageArea.addEventListener('pointerdown', (e) => {
        if (this.inputActive) return;
        // Ignore taps that originate on a tab button or other
        // interactive child — those have their own handlers.
        const target = e.target as HTMLElement | null;
        if (target && target !== this.messageArea && target.tagName === 'BUTTON') {
          return;
        }
        this.activateInput();
      });
    }
    this.root.appendChild(this.messageArea);

    this.dmTargetRow = this.el('div', {
      display: 'none',
      alignItems: 'center',
      gap: '4px',
      padding: '3px 6px',
      background: 'rgba(0,8,0,0.85)',
      borderLeft: `1px solid ${CHANNEL_COLORS[ChatChannel.Direct]}`,
      borderRight: `1px solid ${CHANNEL_COLORS[ChatChannel.Direct]}`,
      fontSize: '10px',
      pointerEvents: 'auto',
    });

    const toLabel = this.el('span', {
      color: CHANNEL_COLORS[ChatChannel.Direct],
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }, 'TO:');
    this.dmTargetRow.appendChild(toLabel);

    this.dmTargetInput = document.createElement('input');
    this.dmTargetInput.type = 'text';
    this.dmTargetInput.maxLength = 32;
    this.dmTargetInput.placeholder = 'PILOT NAME';
    this.css(this.dmTargetInput, {
      flex: '1',
      fontFamily: FONT,
      fontSize: '10px',
      padding: '2px 4px',
      background: 'transparent',
      border: 'none',
      color: CHANNEL_COLORS[ChatChannel.Direct],
      textTransform: 'none',
      letterSpacing: '0.5px',
      outline: 'none',
    });
    this.dmTargetInput.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        this.dmTarget = this.dmTargetInput.value.trim();
        this.input.focus();
      } else if (e.key === 'Escape') {
        this.deactivateInput();
      }
    });
    this.dmTargetInput.addEventListener('input', () => {
      this.dmTarget = this.dmTargetInput.value.trim();
    });
    this.dmTargetRow.appendChild(this.dmTargetInput);
    this.root.appendChild(this.dmTargetRow);

    this.inputRow = this.el('div', {
      display: 'none',
      flexShrink: '0',
      pointerEvents: 'auto',
    });
    this.root.appendChild(this.inputRow);

    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.maxLength = 200;
    this.css(this.input, {
      width: '100%',
      fontFamily: FONT,
      fontSize: '11px',
      padding: '4px 8px',
      background: 'rgba(0,8,0,0.9)',
      border: `1px solid ${COLORS.primary}`,
      borderTop: 'none',
      color: COLORS.primary,
      textTransform: 'none',
      letterSpacing: '0.5px',
      outline: 'none',
      caretColor: COLORS.primary,
      boxShadow: `0 0 6px rgba(0,255,65,0.2)`,
    });

    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submitCurrent();
      } else if (e.key === 'Escape') {
        this.input.value = '';
        this.deactivateInput();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        this.cycleChannel(e.shiftKey ? -1 : 1);
      }
    });

    // Mobile dismissal: on touch devices the pilot can't press Esc,
    // so tapping the game canvas (or anywhere outside the chat panel)
    // is the only natural way to close the chat. iOS/Android fire a
    // `blur` on the <input> the moment the focus leaves, so we
    // piggy-back on that to call `deactivateInput()` — which in turn
    // fires `onInputFocusChange(false)` and re-enables flight input.
    // We gate on `isMobile` so desktop keyboard shortcut workflows
    // (click input → type → click the 3D world to focus ship without
    // losing the input) aren't accidentally broken.
    //
    // The microtask defers the check to *after* the browser has
    // moved focus to its new owner, so shifts *within* the chat
    // (e.g. DM target input → message input when Enter is pressed)
    // don't accidentally dismiss. The `inputActive` guard keeps the
    // handler idempotent with `deactivateInput`'s own `this.input.blur()`.
    if (this.isMobile) {
      const handleBlur = () => {
        if (!this.inputActive) return;
        // Wait until the browser has settled the next focus target,
        // then only deactivate if nothing inside the chat panel owns
        // it. Using a macrotask (0ms setTimeout) plays nicer with
        // iOS WebKit than Promise.resolve() for focus state reads.
        setTimeout(() => {
          if (!this.inputActive) return;
          const active = document.activeElement;
          if (active && this.root.contains(active)) return;
          this.deactivateInput();
        }, 0);
      };
      this.input.addEventListener('blur', handleBlur);
      this.dmTargetInput.addEventListener('blur', handleBlur);
    }

    this.inputRow.appendChild(this.input);

    this.hintRow = this.el('div', {
      flexShrink: '0',
      padding: '2px 8px',
      fontSize: '9px',
      color: COLORS.dim,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      pointerEvents: 'none',
    });
    this.root.appendChild(this.hintRow);

    document.addEventListener('keydown', (e) => {
      if (!this.enabled) return;
      if (this.inputActive) return;
      if (e.key === 'Enter' && !this.isAnyInputFocused()) {
        e.preventDefault();
        this.activateInput();
      }
    });

    this.setActiveChannel(this.activeChannel);
  }

  private isAnyInputFocused(): boolean {
    const active = document.activeElement as HTMLElement | null;
    if (!active) return false;
    if (active === this.input || active === this.dmTargetInput) return true;
    const tag = active.tagName;
    return (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      active.isContentEditable
    );
  }

  private setActiveChannel(ch: ChatChannel): void {
    this.activeChannel = ch;
    this.updateTabs();
    this.updateHint();
    this.renderMessages();
    this.dmTargetRow.style.display =
      ch === ChatChannel.Direct && this.inputActive ? 'flex' : 'none';
  }

  private cycleChannel(dir: 1 | -1): void {
    const idx = VISIBLE_CHANNELS.indexOf(this.activeChannel);
    const next =
      VISIBLE_CHANNELS[
        (idx + dir + VISIBLE_CHANNELS.length) % VISIBLE_CHANNELS.length
      ];
    this.setActiveChannel(next);
  }

  private updateTabs() {
    for (const [ch, btn] of this.tabButtons) {
      const color = CHANNEL_COLORS[ch];
      if (ch === this.activeChannel) {
        btn.style.color = color;
        btn.style.borderColor = color;
        btn.style.textShadow = `0 0 4px ${color}`;
        btn.style.background = COLORS.bg;
      } else {
        btn.style.color = COLORS.dim;
        btn.style.borderColor = COLORS.dim;
        btn.style.textShadow = 'none';
        btn.style.background = 'transparent';
      }
    }
  }

  private updateHint(): void {
    if (this.activeChannel === ChatChannel.Direct) {
      this.hintRow.textContent = t('chat.dmHint');
    } else {
      this.hintRow.textContent = t('controls.chat');
    }
  }

  private activateInput() {
    this.inputActive = true;
    this.inputRow.style.display = 'block';
    if (this.activeChannel === ChatChannel.Direct) {
      this.dmTargetRow.style.display = 'flex';
      if (!this.dmTarget) {
        this.dmTargetInput.focus();
        this.input.placeholder = t('chat.dmEmpty');
      } else {
        this.input.focus();
        this.input.placeholder = `/w ${this.dmTarget}`;
      }
    } else {
      this.input.focus();
      this.input.placeholder = '';
    }
    this.onInputFocusChange?.(true);
    this.revealAndResetFade();
  }

  private deactivateInput() {
    this.inputActive = false;
    this.inputRow.style.display = 'none';
    this.dmTargetRow.style.display = 'none';
    this.input.blur();
    this.dmTargetInput.blur();
    this.onInputFocusChange?.(false);
    this.scheduleFade();
  }

  private submitCurrent(): void {
    const raw = this.input.value.trim();
    this.input.value = '';

    if (!raw) {
      this.deactivateInput();
      return;
    }

    const dm = DM_COMMAND.exec(raw);
    if (dm) {
      const target = dm[2];
      const content = dm[3];
      if (target && content) {
        this.dmTarget = target;
        this.dmTargetInput.value = target;
        this.sendMessage?.(content, ChatChannel.Direct, target);
        this.appendLocalEcho(ChatChannel.Direct, content, target);
      }
      this.setActiveChannel(ChatChannel.Direct);
      this.deactivateInput();
      return;
    }

    if (this.activeChannel === ChatChannel.Direct) {
      const target = this.dmTarget.trim();
      if (!target) {
        this.pushSystemError('chat.dmEmpty');
        return;
      }
      this.sendMessage?.(raw, ChatChannel.Direct, target);
      this.appendLocalEcho(ChatChannel.Direct, raw, target);
    } else {
      this.sendMessage?.(raw, this.activeChannel);
      this.appendLocalEcho(this.activeChannel, raw);
    }

    this.deactivateInput();
  }

  private appendLocalEcho(
    channel: ChatChannel,
    content: string,
    dmPartner?: string,
  ): void {
    // Global / Sector / Fleet echoes come back from the server broadcast, so
    // we don't want to duplicate them. Direct messages are echoed by the
    // server too (to show them in the sender's own DM thread), so the same
    // applies. We intentionally skip the local echo to avoid duplicates.
    void channel;
    void content;
    void dmPartner;
  }

  private renderMessages() {
    this.messageArea.innerHTML = '';

    const filtered = this.messages.filter((m) => {
      if (m.channel === ChatChannel.System) return true;
      return m.channel === this.activeChannel;
    });

    if (filtered.length === 0) {
      const empty = this.el('div', {
        color: COLORS.dim,
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }, t('chat.empty'));
      this.messageArea.appendChild(empty);
      return;
    }

    const visible = filtered.slice(-MAX_MESSAGES);
    for (const msg of visible) {
      this.messageArea.appendChild(this.renderLine(msg));
    }
    this.messageArea.scrollTop = this.messageArea.scrollHeight;
  }

  private renderLine(msg: StoredMessage): HTMLElement {
    const line = this.el('div', {
      fontSize: '10px',
      lineHeight: '1.4',
      textTransform: 'none',
      wordBreak: 'break-word',
    });

    const channelColor = CHANNEL_COLORS[msg.channel];

    const tag = this.el('span', {
      color: channelColor,
      marginRight: '4px',
      textTransform: 'uppercase',
    }, `[${t(CHANNEL_KEYS[msg.channel])}]`);
    line.appendChild(tag);

    if (msg.channel === ChatChannel.Direct) {
      const partner = msg.dmPartner ?? '';
      const arrow = msg.fromLocal ? `→ ${partner}` : `← ${partner}`;
      const dir = this.el('span', {
        color: channelColor,
        marginRight: '4px',
      }, `${arrow}:`);
      line.appendChild(dir);
    } else if (msg.channel === ChatChannel.System) {
      const isActivity = msg.system?.kind === 'activity';
      const labelColor =
        isActivity && msg.system?.kind === 'activity' && msg.system.color
          ? msg.system.color
          : COLORS.system;
      const label = this.el('span', {
        color: labelColor,
        marginRight: '4px',
      }, isActivity ? '>' : 'SYS:');
      line.appendChild(label);
    } else if (msg.senderName) {
      if (msg.premium) {
        // Gold ★ before the callsign for Premium subscribers. Tight
        // letter-spacing keeps it visually attached to the name so it
        // reads as part of the speaker tag rather than a stray glyph.
        const star = this.el('span', {
          color: '#ffd700',
          textShadow: '0 0 4px rgba(255,215,0,0.6)',
          marginRight: '2px',
        }, '★');
        line.appendChild(star);
      }
      const name = this.el('span', {
        color: msg.fromLocal ? channelColor : COLORS.primary,
        marginRight: '4px',
      }, `${msg.senderName}:`);
      line.appendChild(name);
    }

    const contentColor =
      msg.channel === ChatChannel.System
        ? msg.system?.kind === 'activity' && msg.system.color
          ? msg.system.color
          : COLORS.system
        : 'rgba(0,255,65,0.85)';
    const content = this.el('span', {
      color: contentColor,
    }, this.localizeSystem(msg));
    line.appendChild(content);

    return line;
  }

  private localizeSystem(msg: StoredMessage): string {
    if (msg.channel !== ChatChannel.System) return msg.content;
    if (msg.system?.kind === 'error') {
      return t(msg.system.key, msg.system.params);
    }
    if (msg.system?.kind === 'info') return msg.system.text;
    if (msg.system?.kind === 'activity') return msg.system.text;
    return msg.content;
  }

  private scheduleFade() {
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
    this.fadeTimer = setTimeout(() => {
      if (!this.inputActive) {
        this.root.style.opacity = '0.25';
      }
    }, FADE_TIMEOUT);
  }

  private revealAndResetFade() {
    this.root.style.opacity = '1';
    this.scheduleFade();
  }

  private pushSystemError(key: string, params?: Record<string, string>): void {
    this.messages.push({
      senderName: '',
      channel: ChatChannel.System,
      content: key,
      timestamp: Date.now(),
      fromLocal: false,
      system: { kind: 'error', key, params },
    });
    this.renderMessages();
    this.revealAndResetFade();
  }

  /* ═══════════════ PUBLIC API ═══════════════ */

  /**
   * Feed a ChatReceive message from the network layer. Handles DM routing
   * (normalises dmPartner to the OTHER party) and self-sent echoes.
   */
  addIncoming(payload: {
    senderId: string;
    senderName: string;
    channel: ChatChannel;
    content: string;
    timestamp: number;
    targetId?: string;
    targetName?: string;
    localPlayerId?: string;
    /**
     * Server-tagged Premium snapshot at fan-out time. Drives the
     * gold ★ badge next to the callsign so a stream of older
     * unstamped messages doesn't lose its history when the sender
     * lapses.
     */
    premium?: boolean;
  }): void {
    const fromLocal =
      (payload.localPlayerId &&
        payload.senderId === payload.localPlayerId) ||
      (this.localName &&
        payload.senderName.toLowerCase() === this.localName.toLowerCase());

    const dmPartner =
      payload.channel === ChatChannel.Direct
        ? fromLocal
          ? payload.targetName ?? ''
          : payload.senderName
        : undefined;

    this.messages.push({
      senderName: payload.senderName,
      channel: payload.channel,
      content: payload.content,
      timestamp: payload.timestamp,
      fromLocal: Boolean(fromLocal),
      dmPartner,
      premium: payload.premium === true,
    });

    if (this.messages.length > MAX_MESSAGES * 2) {
      this.messages = this.messages.slice(-MAX_MESSAGES);
    }

    this.renderMessages();
    this.revealAndResetFade();

    if (payload.channel === ChatChannel.Direct && !fromLocal) {
      // Remember the last sender so the user can hit Enter and reply.
      this.dmTarget = payload.senderName;
      if (this.activeChannel === ChatChannel.Direct) {
        this.dmTargetInput.value = payload.senderName;
      }
    }
  }

  /** Feed a server-side SYSTEM chat message (e.g. dm target offline). */
  addSystemFromServer(reason: string): void {
    if (reason.startsWith('chat_dm_offline:')) {
      const name = reason.slice('chat_dm_offline:'.length);
      this.pushSystemError('chat.dmTargetMissing', { name });
      return;
    }
    if (reason.startsWith('chat_dm_not_friend:')) {
      const name = reason.slice('chat_dm_not_friend:'.length);
      this.pushSystemError('chat.dmNotFriend', { name });
      return;
    }
    if (reason === 'chat_dm_self') {
      this.pushSystemError('chat.dmSelf');
      return;
    }
    if (reason === 'chat_dm_missing_target') {
      this.pushSystemError('chat.dmEmpty');
      return;
    }
    if (reason.startsWith('chat_rejected:rate_limited')) {
      this.pushSystemError('chat.rateLimited');
      return;
    }
    // Fallback: raw reason
    this.messages.push({
      senderName: '',
      channel: ChatChannel.System,
      content: reason,
      timestamp: Date.now(),
      fromLocal: false,
      system: { kind: 'info', text: reason },
    });
    this.renderMessages();
    this.revealAndResetFade();
  }

  /**
   * Append a local activity line (XP / pickup / warp / error toast / etc.) to
   * the chat transcript. Historically these were short-lived DOM "floating
   * messages" stacked on the right side of the HUD; routing them through
   * chat instead gives pilots a scrollable log of recent events and frees
   * up precious screen real estate on mobile. The line lives on the System
   * channel so it shows up regardless of which tab is active, and the
   * optional `color` is rendered on the `>` marker + body text so the same
   * semantic colors as the old pill notifications carry over.
   */
  addActivity(text: string, color?: string): void {
    this.messages.push({
      senderName: '',
      channel: ChatChannel.System,
      content: text,
      timestamp: Date.now(),
      fromLocal: false,
      system: { kind: 'activity', text, color },
    });
    if (this.messages.length > MAX_MESSAGES * 2) {
      this.messages = this.messages.slice(-MAX_MESSAGES);
    }
    this.renderMessages();
    this.revealAndResetFade();
  }

  openDMWith(name: string): void {
    this.dmTarget = name;
    this.dmTargetInput.value = name;
    this.setActiveChannel(ChatChannel.Direct);
    this.activateInput();
    this.input.focus();
  }

  toggleInput() {
    if (this.inputActive) this.deactivateInput();
    else this.activateInput();
  }

  isInputFocused(): boolean {
    return this.inputActive;
  }

  show() {
    this.enabled = true;
    this.root.style.display = 'flex';
    this.updateTabs();
    this.renderMessages();
    this.revealAndResetFade();
  }

  hide() {
    this.enabled = false;
    this.root.style.display = 'none';
    if (this.inputActive) this.deactivateInput();
  }
}
